import {Component, Element, Event, type EventEmitter, h, Listen, Method, Prop, State, Watch,} from '@stencil/core';
import {BASE_URLS, CONVERSATIONAL_SEARCH, SEARCHERS} from '../../../../strategies/utils/variables';
import type {ISearchersStrategyService} from '../../../../strategies/SearcherNamesStrategyService';
import {isLargeDesktop, isMobile} from '../../../../../utils/media';
import {mapWidgetData} from '../../../../widgets/offsites-widget/utils/map-widget-data';
import {widgetData} from '../../../../widgets/offsites-widget/assets/offsites-assets/config.json';
import type {SearchResultClickEvent} from '../../SearchResultClickEvent';
import type {ResultRenderer} from '../../../../../utils/Renderer';
import {makeLogger} from '../../../../../logger/logger';
import type {SearchApi, SearchApiDocument, SearchFaqDocument, SearchProduct} from '../../../../../api/SearchApi';
import {ITEMS_PER_PAGE} from '../../../../../constants/paginationConstants';
import {FacetsService} from '../../../../../utils/services/facets/facets.service';
import {getValues} from '../../../../widgets/exchange-community/exchange-community-actions';
import type {PipesRegistry} from '../../../../../utils/middleware-pipe/models/stack-pipes-registry.model';
import {Strategies, StrategiesFactory,} from '../../../../strategies/Strategies';
import type {MiddlewareSearchPipe} from '../../../../../utils/middleware-pipe/middleware-pipe';
import {DashboardDesktopView} from './dashboard-views/dashboard-desktop-view';
import {DashboardMobileView} from './dashboard-views/dashboard-mobile-view';
import {appendSectionToTabName, transformTabName} from './utils/utils';
import {MySeDashboardDesktopView} from './dashboard-views/myse-dashboard-view/myse-dashboard-desktop-view';
import type {ConversationalSearchStrategyService} from '../../../../strategies/ConversationalSearchStrategyService';
import ProductSidePanelService from '../../../product-side-panel/product-side-panel.service';
import {handleLazyLoadImage, makeTabName} from '../../util';
import type {IEolDiscontinuedDetails} from '../types/subcomponents-types.type';
import {EolResult} from '../../../../../constants/eolResult';
import {getSiteSearchCategory} from '../../../../analytics-reporting/util';
import type {
  IConversationalSearchStrategyData
} from '../../../../strategies/types/conversational-search-srategy-data.type';
import type {SectionsData} from '../../../../../api/model/response/documents/SearchResult';
import type {BaseUrlsStrategyService} from '../../../../strategies/BasePathStrategyService';
import type {BaseUrlsStrategyData} from '../../../../strategies/types/base-urls-data.type';
import {readComponentsConfig} from './dashboard-views/create-dashboard-grid';
import type {DashboardDataFlux} from '../../dataflux';
import type {UiaasSearchDataFlux} from '../../../search/dataflux';
import EventBus from '../../../../../utils/libs/eventbus/eventbus';
import DataFlux from '../../../../../utils/libs/dataflux/dataflux';
import {location} from '../../../../../constants/pageLocation';

const {info, warn, err} = makeLogger({
  pathToCurrentFile:
        'components/result-page/search-tab/subcomponents/dashboard-tab-view/dashboard-tab-view-v2.tsx',
});

@Component({
  tag: 'dashboard-tab-view-v2',
  shadow: true,
  styleUrl: 'dashboard-tab-view.scss',
})
export class DashboardTabViewV2 {
  private currentPage = 1;
  private strategies: Strategies;
  private analyticsTabName: string;
  private relatedProductName: string;
  private uiaasThumbnails = [];
  private tabs = [];
  private desktopLayout;

  // used in middleware - need to refactore
  private facetService!: FacetsService;

  /**
     Contain gathered section data
     **/
  private searchsDataBundled:SectionsData = {};
  private conversationalSearchStrategyData: IConversationalSearchStrategyData['params'];
  private _baseUrls:BaseUrlsStrategyData;
  private productNaLocales = '';

  @Prop() readonly timeout?: number;
  @Prop() readonly isDashboardView: boolean;
  @Prop() readonly country: string;
  @Prop() readonly language: string;
  @Prop({mutable: true}) mobileView = isMobile();
  @Prop({mutable: true}) labelProvider;
  @Prop() readonly isEcommerceMode: boolean;

  @Prop() readonly eShop;
  @Prop() readonly rtl: boolean;
  @Prop() readonly hasSupportButton: boolean = false;
  @Prop() readonly hasCtaProductSelector: boolean = false;
  @Prop() readonly hasOffsites = false;
  @Prop() readonly widgetType: string;
  @Prop({mutable: true}) tabName: string;
  @Prop() readonly reflectedTabs: Object;
  @Prop() readonly appName: string;
  @Prop() readonly isSupport?: boolean;
  @Prop() readonly isAutocorrectEndpoint?: boolean = false;
  @Prop() readonly runAutocorrect?: boolean = false;
  @Prop({mutable: true}) reflectedState;
  @Prop() readonly tabNumber: number;
  @Prop() readonly tabTitle?: string;
  @Prop() readonly resultClickHandler?;
  @Prop({mutable: true}) isVisible: boolean = false; //eslint-disable-line @stencil-community/strict-mutable
  @Prop() readonly resultRenderer?: ResultRenderer;
  @Prop() readonly lazyLoadImages: boolean = false;
  @Prop() readonly layout: string;
    @Prop({attribute: 'apigee-auth'}) readonly apigeeAuth: string;
    @Prop({attribute: 'apigee-token-url'}) readonly apigeeTokenUrl: string;
    @Prop({attribute: 'token'}) readonly token: string;
    @Prop({attribute: 'api-key'}) readonly apiKey: string;
    @Prop({attribute: 'apigee-bearer'}) readonly apigeeBearer: string;
    @Prop({attribute: 'api-endpoint'}) readonly apiEndpointProp: URL | string;

    @Prop({attribute: 'query', mutable: true}) queryProp: string; //eslint-disable-line @stencil-community/strict-mutable

    @Prop() readonly middlewares: string;
    @Prop() readonly perPage: number = ITEMS_PER_PAGE;
    @Prop() readonly externalMiddleware?: MiddlewareSearchPipe<any>;//eslint-disable-line @typescript-eslint/no-explicit-any
    @Prop() readonly externalMiddlewareName?: string;
    @Prop({attribute: 'preload-data'}) readonly preloadDataAttribute: string;
    @Prop({attribute: 'price-banner'}) readonly priceBanner: boolean = false;
    @Prop({attribute: 'product-catalog-banner'}) readonly productCatalogBanner: boolean = false;
    @Prop() readonly isSubstitutionTool: boolean;
    @Prop() readonly isOffsiteBannersManager: boolean;
    @Prop() readonly offsitesViewOption: 'full-width' | undefined = undefined;
    @Prop() readonly isConversationalSearchActive: boolean = false;

    /**
    * Is Unified Checkout integration enabled
    */
    @Prop() readonly isUnifiedCheckout?: boolean = false;

    private dataflux = new DataFlux<DashboardDataFlux>({
      eventbus: new EventBus()
    });
    @Prop() readonly parentDataFlux:DataFlux<UiaasSearchDataFlux>;

    /**
     * Enables smart country selector
     *! Remove after global decommissioning
     */
    @Prop() readonly globalDecommissioning: boolean = false;

    /**
    * Is Additional Conditions Widget is on=true or off=false.
    */
    @Prop() readonly isAdditionalConditionsWidgetActive?: boolean;

    @Prop() readonly isMiaPromotionWidget?: boolean;

    @State() skipAutocorrection?: boolean = false;
    @State() apiEndpoint: URL;
    @State() query = '';
    @State() queryResult: SearchApi | any;//eslint-disable-line @typescript-eslint/no-explicit-any
    @State() middlewareRegistry: PipesRegistry = {};
    @State() eolDiscontinuedResults: IEolDiscontinuedDetails = {documents: [], documentsGroup: [], replacementsTotalHits: 0};
    @State() animationFramed = false;

    private handleDashboardRePaint = () => {
        if (this.isInitialPageLoad) return this.animationFramed = true;

        const startTime = performance.now();

        const checkTabsLoading = () => {
            const endTime = performance.now();
            const elapsedTime = endTime - startTime;
            //Repaint if tabs are loading longer than 3s
            if (!this.tabsAreLoading || elapsedTime > 3000) {
              this.animationFramed = true;
            } else {
              requestAnimationFrame(checkTabsLoading);
            }
        };

        requestAnimationFrame(checkTabsLoading);
    };

    @Element() el;

    get baseUrls(): BaseUrlsStrategyData {
        return this._baseUrls;
    }

    set baseUrls(value: BaseUrlsStrategyData) {
        this._baseUrls = value;
    }

    @Method()
    @Listen('search:reset')
    async reset() {
        this.currentPage = 1;
    }

    private getQuery() {
        return this.skipAutocorrection ? this.queryProp : this.query;
    }

    @Listen('search:autocorrectquery', {target: 'window'})
    searchAutocorrectQueryUpdate(event: CustomEvent): void {
        if (event.detail.changeQuery === true) {
          this.skipAutocorrection = true;
        }
    }
    @Listen('search:autocorrect', {target: 'window'})
    updateAutocorrectQuery({detail}) {
        if (detail.isAutocorrect && detail.suggestion) {
          this.query = detail.suggestion;
        }
    };

    constructor() {
        this.resultClickAnalytics = this.resultClickAnalytics.bind(this);
    }

    connectedCallback() {
        this.analyticsTabName = transformTabName(this.tabName);
    }

    componentWillLoad() {
        document.querySelectorAll('uiaas-search-tab').forEach(e=> {
            const tabName = e.getAttribute('tab-name');
            this.tabs.push(tabName.split('/').pop());
        } );
        this.updateQuery();
        if (!this.labelProvider) {
          info('Assigning default labelProvider to SearchTab component');

          this.labelProvider = (name, fallback) => {
              warn('LabelProvider not provided in SearchTab component', [
                name,
                fallback,
              ]);
              return fallback;
          };
        }

        if (!this.tabName) {
          this.tabName = makeTabName(this.tabTitle);
        }

        this.reflectedState = {
          tab: this.tabName,
          ajaxIsLoading: false,
          hasError: false,
          count: 0,
        };

        this.strategies = StrategiesFactory.createStrategy(this.el);
        this.getConversationalSearchConfigForActiveTab();
        // used in middleware - need to refactore
        this.facetService = FacetsService.getSetInstance('tab');

        try {
          const layoutArr = this.layout.split('|');
          this.desktopLayout = readComponentsConfig(layoutArr);
        } catch (e) {
          err('Error while parsing layout', e);
        }

        this.isInitialPageLoad = true;
    }



    @Event({
      eventName: 'search:SearchResultClickEvent',
      composed: true,
      cancelable: true,
      bubbles: true,
    })
      searchResultClickEmitter: EventEmitter<SearchResultClickEvent>;

    @Listen('resize', {target: 'window'})
    @Listen('load', {target: 'window'})
    handleResize() {
        this.mobileView = isMobile();
        info('Mobile', this.mobileView);
    }

    // used in carousel - need to move it
    private resultClickAnalytics(
      e: MouseEvent,
      index,
      url,
      commercialStatus,
      section?
    ) {
        const tabName = appendSectionToTabName(this.analyticsTabName, section);

        e.preventDefault();
        this.searchResultClickEmitter.emit({
          event: 'searchResultClick',
          appName: this.appName,
          searchPagePosition: this.currentPage,
          searchResultPosition: index,
          site_search_category: getSiteSearchCategory(tabName, this.appName),
          siteSearchKeyword: this.getQuery(),
          is_filter_use: false,
          rightButton: e.button !== 0,
          openUrl: url,
          clickedArea: DashboardTabViewV2.getClickArea(e),
          tabName: tabName,
          clickType: this.getClickType(e),
          commStatus: commercialStatus,
          product: window.location.hostname,
          country: this.country,
          language: this.language,
          endpoint: this.apiEndpoint,
          apigeeTokenUrl: this.apigeeTokenUrl,
          apigeeAuth: this.apigeeAuth,
          searcher: 'se/all',
        });

        return this.resultClickHandler(e);
    }

    private filterEolDiscontinuedResults = (type: string, documents: Array<SearchApiDocument>): Array<SearchApiDocument> => {
        if (type.includes('products') && this.isSubstitutionTool) {
          const replacementsTotalHits = documents.filter(doc => doc.productEolReplacementStatus && doc.productEolReplacementStatus === EolResult.SUBSTITUTE).length;
          this.eolDiscontinuedResults = {
            documents: documents.filter(doc => doc.productEolReplacementStatus && doc.productEolReplacementStatus === EolResult.DISCONTINUED),
            documentsGroup: documents.filter(doc => doc.productEolReplacementStatus && doc.productEolReplacementStatus === EolResult.DISCONTINUED_GROUP),
            replacementsTotalHits: replacementsTotalHits
          };
          return replacementsTotalHits ? documents.filter(doc => !doc.productEolReplacementStatus ||
            (doc.productEolReplacementStatus !== EolResult.DISCONTINUED &&
            doc.productEolReplacementStatus !== EolResult.DISCONTINUED_GROUP)) : documents;
        }
        return documents;
    };

    private getDocumentsWithLimits = (type: string, limit: number): Array<any> => {//eslint-disable-line @typescript-eslint/no-explicit-any
        if (this.reflectedTabs?.[type]?.documents?.length) {
          return this.filterEolDiscontinuedResults(type, this.reflectedTabs?.[type]?.documents.slice(0, limit));
        }
        return [];
    };

    private getTabCount = (type) => {
        if (this.reflectedTabs?.[type]?.count) {
          return this.reflectedTabs?.[type]?.count;
        }
        return 0;
    };
    private getTabCountMySE = () => {
        let sum = 0;
        const countedTabs = {};
        if (this.reflectedTabs) {
          for (const key in this.reflectedTabs) {
            if (this.reflectedTabs[key].tab !== 'se/all' && !countedTabs[key]) {
              sum += this.reflectedTabs[key].count;
              countedTabs[key] = true;
            }
          }
        }

        return sum;
    };

    private setDataForFaq = (): Array<FAQ> => {
        const searcherData = (this.searchsDataBundled as { [key: string]: SearchFaqDocument[] }).faq;
        const bundledData = searcherData?.map((object) => {
            const {description, faqID, title, url} = object;
            return {
              id:faqID,
              question: title,
              answer: description,
              url,
            };
        });

        return bundledData;
    };

    private renderOffsites = (brand) => {
        const offsitesWidgets = mapWidgetData(
          widgetData,
          this.country,
          this.language,
          this.getQuery(),
          brand,
          this.tabName,
          this.relatedProductName
        );

        // Special case for Aveva brand promotion
        // To be removed when Aveva documents will be indexed!
        const isAvevaType = offsitesWidgets.widgetType === 'aveva';

        if (offsitesWidgets?.backgroundUrl && !isAvevaType && !this.offsitesViewOption) {
          return isLargeDesktop();
        }

        return Object.keys(offsitesWidgets).length > 0;
    };

    private getCurrentState = () => {
        return {
          term: this.getQuery(),
          perPage: this.perPage,
          threshold: 4,
          mockedData: [],
          apiEndpoint: this.apiEndpointProp,
          apigeeAuth: this.apigeeAuth,
          apigeeTokenUrl: this.apigeeTokenUrl,
        };
    };

    private checkExchangeWidgetVisibility(hasExchangeWidgetAnswers) {
        return hasExchangeWidgetAnswers?.length > 0;
    }
    private bundleSectionData = (searcherNames: { [key: string]: string }, defaultLimit: number = 2) => {

        if (searcherNames) {
          const dataLimits = Object?.keys(searcherNames)?.map((name, index) => {
              let limit;
              switch(true) {
              case name.includes('products'):
                limit = 15;
                break;
              case name.includes('faq'):
                limit = 3;
                break;
              case name.includes('video'):
                limit = 2;
                break;
              default:
                limit = defaultLimit;
              }
              return {
                name,
                limit,
              };
          });


          this.searchsDataBundled = {
            ...this.searchsDataBundled,
            ...dataLimits.reduce(
              (obj, {name, limit}) => {
                  obj[name] = this.getDocumentsWithLimits(searcherNames[name], limit);
                  return obj;
              }, {})
          };
        }
    };

    private getConversationalSearchConfigForActiveTab() {
        const conversationalSearchData: ConversationalSearchStrategyService = this.strategies.for(CONVERSATIONAL_SEARCH);

        this.conversationalSearchStrategyData = conversationalSearchData.getConversationalSearchConfigurationForActiveTab(this.tabName)?.params;
    }

    private getBrandFromStrategy(): string {
        const strategy = this.strategies.for(
          SEARCHERS
        ) as ISearchersStrategyService;
        const strategyResult = strategy.mapSearchersToDashboard();

        return strategyResult?.brand;
    }

    @Listen('search:SearchResultsRelatedProduct', {target: 'window'})
    handleRelatedProduct(event) {
        this.relatedProductName = event.detail.relatedProduct;
    }

    @Prop() readonly debounceImageTime: number = 50;

    private debounce: NodeJS.Timeout;

    @Listen('WCLoaded', {target: 'document'})
    handleWCLoaded(e) {
        this.parentDataFlux.root.wcEventData = e.detail;
    }

    @Listen('uiaas:thumbnail-loaded')
    handleThumbnailLoaded(e) {
        if (this.lazyLoadImages) {
          this.uiaasThumbnails.push(e.detail);
          handleLazyLoadImage(e.detail);
        }
    }
    @Listen('scroll', {target: 'window'})
    @Listen('uiaas:carousel-scrolled')
    handleScroll() {
        if (this.lazyLoadImages && this.isVisible) {
          if (this.debounce !== null) {
            clearTimeout(this.debounce);
            this.debounce = null;
          }
          this.debounce = setTimeout(() => {
              this.uiaasThumbnails.forEach((element => {
                  handleLazyLoadImage(element);
              }));

              this.debounce = null;
          }, this.debounceImageTime);
        }
    }

    private isInitialPageLoad = false;


    @Watch('reflectedTabs')
    handleReflectedTabs(e) {
        this.tabsAreLoading = Object.keys(e).some((key) => e[key].ajaxIsLoading);
        if (Object?.keys(e)?.length === this.tabs?.length + 1 && !this.tabsAreLoading) {
          // changing isInitialPageLoad after first full results load
          this.isInitialPageLoad = false;
          const brand = this.getBrandFromStrategy();
          if (brand === 'myschneider') {
            this.reflectedTabs['se/all'].count = this.getTabCountMySE();
          }
        }
    }
    @Watch('queryProp')
    updateQuery() {
        this.query = this.queryProp;
        this.animationFramed = false;
        this.tabsAreLoading = true;
    }

    componentShouldUpdate() {
        if (!this.tabsAreLoading || this.isInitialPageLoad) {
          this.handleDashboardRePaint();
        }
    }

    private hasExchangeWidgetAnswers = [];
    private successCallback = (res) => {
        this.hasExchangeWidgetAnswers = res;
    };

    private tabsAreLoading = false;


    render() {
        this.isVisible = this.parentDataFlux?.root.activeTab === this.tabNumber;
        let searcherNames;
        const strategy = this.strategies.for(
          SEARCHERS
        ) as ISearchersStrategyService;
        const baseUrlsData: BaseUrlsStrategyService = this.strategies.for(BASE_URLS);
        this.baseUrls = baseUrlsData.getBaseUrls();
        const visibility = this.isVisible ? 'visible' : 'hidden';
        let isExchangeVisible;
        const hasExchangeWidget = this.desktopLayout?.some((item) => item?.name === 'ExchangeCommunityWidget');
        const strategyResult = strategy.mapSearchersToDashboard();
        const brand = strategyResult?.brand;
        let defaultDocumentLimit = 2;
        searcherNames = strategyResult?.predefinedSearchers;
        let offsiteBannersManagerRequestData;
        if (brand != 'myschneider' && hasExchangeWidget) {
          getValues(this.getCurrentState(), this.successCallback);
        }

        if(this.isOffsiteBannersManager) {
          offsiteBannersManagerRequestData = {
            term: this.getQuery(),
            country: this.country,
            language: this.language,
            brand: brand,
            apiEndpoint: this.apiEndpointProp,
            apigeeAuth: this.apigeeAuth,
            apigeeTokenUrl: this.apigeeTokenUrl,
            apikey: this.apiKey,
            idmsToken: this.token
          };
        }

        if (brand === 'myschneider') {
          defaultDocumentLimit = 4;
        }

        if (this.isDashboardView) {
          isExchangeVisible = this.checkExchangeWidgetVisibility(this.hasExchangeWidgetAnswers);
        }

        const isOffsitesVisible = this.isOffsiteBannersManager
          ? true
          : this.hasOffsites && this.renderOffsites(brand);

        const isProductNaCountrySelector = this.desktopLayout?.some(item => item?.name === 'ProductNaCountrySelector');

        this.bundleSectionData(searcherNames, defaultDocumentLimit);

        const faqData = this.setDataForFaq();

        // Code below checks if global decommissioning is active,
        // And if there are any results after the search action
        //! To remove after global decommissioning
        const smartCountrySelectorElement =
            this.globalDecommissioning ? (
              <div class="smart-country-selector__wrapper">
                <uiaas-smart-country-selector-banner
                  isSrpVariant={true}
                  language={this.language}
                  labelProvider={this.labelProvider}
                  totalHits={this.reflectedTabs?.[searcherNames?.all]?.count}
                />
              </div>
            ) : (
              ''
            );

        const productCountrySelectorElement =
        isProductNaCountrySelector && this.parentDataFlux?.root?.productNaCountrySelectorLocales && (
          <div class="smart-country-selector__wrapper">
            <uiaas-product-na-country-selector-banner
              country={this.country}
              language={this.language}
              labelProvider={this.labelProvider}
              totalHits={this.reflectedTabs?.[searcherNames?.all]?.count}
              productNaLocales={this.parentDataFlux?.root?.productNaCountrySelectorLocales}
              isHeaderVisible={true}
              tabName={this.tabName}
              location={location.SRP}
              rtl={this.rtl}
            />
          </div>
        );

        const dashboardButtonText = this.labelProvider(
          'tab.dashboard.view-all',
          'View All'
        );
        const dashboardSubTitleText = this.labelProvider(
          'dashboard.tile.description',
          'You can also browse all the results by clicking on the tab corresponding to this section or on View All.'
        );

        const getActiveTabContent = () => this.isInitialPageLoad || this.animationFramed ? (
          <div
            class={{ [visibility]: true, 'search-tab--rtl': this.rtl, 'dashboard-view-main-content':true }}
            dir={this.rtl ? 'rtl' : 'auto'} id={'panel' + this.tabNumber}
          >
            {this.mobileView && !this.isSupport ? (
              <DashboardMobileView
                isDashboardView={this.isDashboardView}
                reflectedTabs={this.reflectedTabs}
                searcherNames={searcherNames}
                strategies={this.strategies}
                resultRenderer={this.resultRenderer}
                resultClickAnalytics={this.resultClickAnalytics}
                labelProvider={(e, f) => this.labelProvider(e, f)}
                country={this.country}
                language={this.language}
                tabName={this.tabName}
                isEcommerceMode={this.isEcommerceMode}
                eShop={this.eShop}
                rtl={this.rtl}
                isOffsitesVisible={isOffsitesVisible}
                isExchangeVisible={isExchangeVisible}
                widgetType={this.widgetType}
                queryProp={this.getQuery()}
                hasCtaProductSelector={this.hasCtaProductSelector}
                hasSupportButton={this.hasSupportButton}
                apigeeAuth={this.apigeeAuth}
                apigeeTokenUrl={this.apigeeTokenUrl}
                faqData={faqData}
                query={this.getQuery()}
                getTabCount={this.getTabCount}
                analyticsTabName={this.analyticsTabName}
                relatedProductName={this.relatedProductName}
                smartCountrySelectorElement={smartCountrySelectorElement}
                globalDecommissioning={this.globalDecommissioning}
                isProductNaCountrySelector={isProductNaCountrySelector}
                productNaCountrySelectorLocales={this.parentDataFlux?.root?.productNaCountrySelectorLocales}
                wcEventData={this.parentDataFlux?.root?.wcEventData}
                isUnifiedCheckout={this.isUnifiedCheckout}
                productCountrySelectorElement={productCountrySelectorElement}
                totalHits={this.reflectedTabs?.[searcherNames?.all]?.count}
                offsiteBannersManagerRequestData={offsiteBannersManagerRequestData}
                offsitesViewOption={this.offsitesViewOption}
                dashboardButtonText={dashboardButtonText}
                dashboardSubTitleText={dashboardSubTitleText}
                brand={brand}
                sectionsData={this.searchsDataBundled}
                lazyLoadImages={this.lazyLoadImages}
                isSubstitutionTool={this.isSubstitutionTool}
                eolDiscontinuedResults={this.eolDiscontinuedResults}
                conversationalSearchProductsEndpoint={this.conversationalSearchStrategyData.conversationalSearchProductsEndpoint}
                isConversationalSearchActive={this.conversationalSearchStrategyData.isConversationalSearchActive}
                conversationalSearchBrandUrl={this.conversationalSearchStrategyData.conversationalSearchBrandUrl}
                conversationalSearchMedaliaFormId={this.conversationalSearchStrategyData.conversationalSearchMedaliaFormId}
                tabs={this.tabs}
                isAdditionalConditionsWidgetActive={this.isAdditionalConditionsWidgetActive}
                isMiaPromotionWidget={this.isMiaPromotionWidget}
                miaData={this.parentDataFlux?.root?.miaData}
              />) : brand == 'myschneider' ? (
              <MySeDashboardDesktopView
                reflectedTabs={this.reflectedTabs}
                searcherNames={searcherNames}
                strategies={this.strategies}
                resultRenderer={this.resultRenderer}
                resultClickAnalytics={this.resultClickAnalytics}
                labelProvider={(e, f) => this.labelProvider(e, f)}
                country={this.country}
                language={this.language}
                tabName={this.tabName}
                rtl={this.rtl}
                queryProp={this.getQuery()}
                query={this.getQuery()}
                getTabCount={this.getTabCount}
                priceBanner={this.priceBanner}
                productCatalogBanner={this.productCatalogBanner}
                brand={brand}
                // @ts-ignore
                sectionsData={this.searchsDataBundled}
                faqData={faqData}
                isOffsitesVisible={isOffsitesVisible}
                offsiteBannersManagerRequestData={offsiteBannersManagerRequestData}
                relatedProductName={this.relatedProductName}
                isSubstitutionTool={this.isSubstitutionTool}
                eolDiscontinuedResults={this.eolDiscontinuedResults}
                isSupport={this.isSupport}
                wcEventData={this.parentDataFlux?.root?.wcEventData}
                isUnifiedCheckout={this.isUnifiedCheckout}
                isAdditionalConditionsWidgetActive={this.isAdditionalConditionsWidgetActive}
                conversationalSearchProductsEndpoint={this.conversationalSearchStrategyData.conversationalSearchProductsEndpoint}
                isConversationalSearchActive={this.isConversationalSearchActive}
                conversationalSearchBrandUrl={this.conversationalSearchStrategyData.conversationalSearchBrandUrl}
                conversationalSearchMedaliaFormId={this.conversationalSearchStrategyData.conversationalSearchMedaliaFormId}
                apigeeAuth={this.apigeeAuth}
                apigeeTokenUrl={this.apigeeTokenUrl}
              />
            ) : (<DashboardDesktopView
              layout={this.desktopLayout}
              isDashboardView={this.isDashboardView}
              reflectedTabs={this.reflectedTabs}
              searcherNames={searcherNames}
              strategies={this.strategies}
              resultRenderer={this.resultRenderer}
              resultClickAnalytics={this.resultClickAnalytics}
              labelProvider={(e, f) => this.labelProvider(e, f)}
              country={this.country}
              language={this.language}
              tabName={this.tabName}
              isEcommerceMode={this.isEcommerceMode}
              eShop={this.eShop}
              rtl={this.rtl}
              isOffsitesVisible={isOffsitesVisible}
              isExchangeVisible={isExchangeVisible}
              widgetType={this.widgetType}
              queryProp={this.getQuery()}
              hasCtaProductSelector={this.hasCtaProductSelector}
              hasSupportButton={this.hasSupportButton}
              faqData={faqData}
              query={this.getQuery()}
              getTabCount={this.getTabCount}
              analyticsTabName={this.analyticsTabName}
              relatedProductName={this.relatedProductName}
              smartCountrySelectorElement={smartCountrySelectorElement}
              productCountrySelectorElement={productCountrySelectorElement}
              totalHits={this.reflectedTabs?.[searcherNames?.all]?.count}
              isProductNaCountrySelector={isProductNaCountrySelector}
              productNaCountrySelectorLocales={this.parentDataFlux?.root?.productNaCountrySelectorLocales}
              wcEventData={this.parentDataFlux?.root?.wcEventData}
              isUnifiedCheckout={this.isUnifiedCheckout}
              globalDecommissioning={this.globalDecommissioning}
              offsitesViewOption={this.offsitesViewOption}
              offsiteBannersManagerRequestData={offsiteBannersManagerRequestData}
              dashboardButtonText={dashboardButtonText}
              dashboardSubTitleText={dashboardSubTitleText}
              brand={brand}
              apigeeAuth={this.apigeeAuth}
              apigeeTokenUrl={this.apigeeTokenUrl}
              sectionsData={this.searchsDataBundled}
              isSupport={this.isSupport}
              lazyLoadImages={this.lazyLoadImages}
              isAdditionalConditionsWidgetActive={this.isAdditionalConditionsWidgetActive}
              isMiaPromotionWidget={this.isMiaPromotionWidget}
              miaData={this.parentDataFlux?.root?.miaData}
              isSubstitutionTool={this.isSubstitutionTool}
              eolDiscontinuedResults={this.eolDiscontinuedResults}
              conversationalSearchProductsEndpoint={this.conversationalSearchStrategyData.conversationalSearchProductsEndpoint}
              isConversationalSearchActive={this.conversationalSearchStrategyData.isConversationalSearchActive}
              conversationalSearchBrandUrl={this.conversationalSearchStrategyData.conversationalSearchBrandUrl}
              conversationalSearchMedaliaFormId={this.conversationalSearchStrategyData.conversationalSearchMedaliaFormId}
              tabs={this.tabs}
            />)}
            <dashboard-analytics
              country={this.country}
              language={this.language}
              tabName={this.tabName}
              appName={this.appName}
              isVisible={this.isVisible}
              apiEndpointProp={this.apiEndpointProp as URL}
              apigeeAuth={this.apigeeAuth}
              apigeeTokenUrl={this.apigeeTokenUrl}
              analyticsTabName={this.analyticsTabName}
              query={this.getQuery()}
              searcher={searcherNames?.all}
              reflectedTabs={this.reflectedTabs}
            />
          </div>
        ) : '';

        if (this.isDashboardView) {
          return [ //eslint-disable-line @stencil-community/render-returns-host
            <dashboard-misspellings
              country={this.country}
              language={this.language}
              api-endpoint={this.apiEndpoint}
              isAutocorrectEndpoint={this.isAutocorrectEndpoint}
              runAutocorrect={this.runAutocorrect}
              tabName={this.tabName}
              reflectedState={this.reflectedState}
              reflectedTab={this.reflectedTabs?.[searcherNames?.all]}
              tabTitle={this.tabTitle}
              resultClickHandler={this.resultClickHandler}
              apigeeAuth={this.apigeeAuth}
              apigeeTokenUrl={this.apigeeTokenUrl}
              token={this.token}
              apiKey={this.apiKey}
              apigeeBearer={this.apigeeBearer}
              apiEndpointProp={this.apiEndpointProp}
              queryProp={this.queryProp}
              middlewares={this.middlewares}
              preloadDataAttribute={this.preloadDataAttribute}
            />,
            this.strategies.strategyData
                && this.searchsDataBundled.hasOwnProperty('products')
                && this.strategies.strategyData.cardType.params?.isSidePanelFeature
                && this.strategies.strategyData.cardType.params?.productSelector ?
              <uiaas-search-product-side-panel-event-wrapper
                tabName={getSiteSearchCategory(this.tabName, this.appName)}
                appName={this.appName}
                country={this.country}
                language={this.language}
                isFilterInUse={false}
                productSelector={this.strategies.strategyData.cardType.params.productSelector}
                myProductsUrl={this.baseUrls.params?.myProductsUrl}
                myProductsDataSheetTemplate={this.baseUrls.params?.myProductsDataSheetTemplate}
                labelProvider={this.labelProvider}
              >
                <uiaas-search-product-side-panel
                  country={this.country}
                  language={this.language}
                  brand={this.strategies.strategyData.cardType.params.brand}
                  searcher={this.tabName}
                  queryResult={this.queryResult}
                  documents={this.searchsDataBundled['products'] as SearchProduct[]}
                  term={this.query}
                  rtl={this.rtl}
                  labelProvider={(e, f) => {
                      return this.labelProvider(e, f);
                  }}
                  productSidePanelService={new ProductSidePanelService(this.apigeeTokenUrl, this.apigeeAuth, this.apiKey, new URL(this.apiEndpointProp))}
                  isSubstitutionTool={this.isSubstitutionTool}
                  apigeeAuth={this.apigeeAuth}
                  apigeeTokenUrl={this.apigeeTokenUrl}
                  apiEndpoint={this.apiEndpointProp}
                ></uiaas-search-product-side-panel>
              </uiaas-search-product-side-panel-event-wrapper>
              : '',
            getActiveTabContent()
          ];
        }
        return '';
    }

    // move to helper
    private static getClickArea(e): string {
        const eventClassList = e.currentTarget.classList;

        if (eventClassList.contains('result-title__link')) {
          return 'title';
        } else if (eventClassList.contains('result__image-placeholder')) {
          return 'image';
        } else if (eventClassList.contains('result__range_link')) {
          return 'range';
        } else if (eventClassList.contains('document-link__link')) {
          return 'attachment';
        } else {
          return 'background';
        }
    }

    // move to helper
    private getClickType(e): string {
        if (e.key === 'Enter') {
          return e.key;
        }
        if (e.button === 0) {
          return 'left';
        } else {
          return 'right';
        }
    }
}

